import React, { useCallback, useEffect, useState } from "react";
import { Box, Checkbox, CircularProgress, Grid, IconButton, Typography } from "@mzc-pdc/ui";
import theme from "@styles/theme";
import { IconCheck, IconCheckDefaultCircle, IconCheckLarge, IconRefresh } from "@mzc-cloudplex/icons";
import formatDuration from "@cores/formatDuration";
import { PUBLIC_URL } from "@/constants";
import { alpha } from "@mui/material";
import { CircledIcon } from "@components_v2/icon";
import { useTranslation } from "react-i18next";

const SelectMultiImage = ({ data, onChange }) => {
    const { t } = useTranslation();
    const [selectedItems, setSelectedItems] = useState([]);
    const getElementThumbnailUrl = useCallback((element) => {
        if (!element?.previews?.[0]?.accessUrl && !element?.accessUrl)
            return `${PUBLIC_URL}/images/thumbnail_no_image_l.png`;
        return element.previews?.[0]?.accessUrl ?? element?.accessUrl;
    }, []);

    const isChecked = useCallback(
        (elementId) => {
            return selectedItems.some((item) => item === elementId);
        },
        [selectedItems],
    );

    const onCheckElement = useCallback(
        (elementId) => {
            let selectedItems_ = [];
            const isChecked = selectedItems.some((item) => item === elementId);
            if (isChecked) selectedItems_ = selectedItems.filter((item) => item !== elementId);
            else selectedItems_ = [...selectedItems, elementId];

            setSelectedItems(selectedItems_);
            onChange(selectedItems_);
        },
        [selectedItems, onChange],
    );

    const onHandleClickCheckAll = useCallback(() => {
        if (selectedItems.length === data.elements?.length) {
            setSelectedItems([]);
            onChange([]);
        }
        if (selectedItems.length < data.elements?.length) {
            setSelectedItems(data.elements?.map((element) => element.id));
            onChange(data.elements?.map((element) => element.id));
        }
    }, [selectedItems, onChange]);

    useEffect(() => {
        if (!data) return;
        setSelectedItems(data.elements?.map((element) => element.id));
    }, []);

    return (
        <Grid container gap={1}>
            <Grid
                container
                flexDirection={"row"}
                sx={{
                    maxHeight: 320,
                    p: 2,
                    overflowY: "auto",
                    // DAM-646: 스크롤바 생길 때 콘텐츠 밀림 방지
                    scrollbarGutter: "stable",
                    backgroundColor: alpha(theme.palette.greySecondary[50], 0.5),
                    borderRadius: theme.spacing(1),
                    gap: "12px",
                }}
            >
                <Grid container gap={1} flexDirection={"row"} alignItems={"center"}>
                    <Checkbox
                        color={"greySecondary"}
                        checked={selectedItems.length > 0}
                        indeterminate={selectedItems.length > 0 && selectedItems.length < data?.elements?.length}
                        onChange={onHandleClickCheckAll}
                    />
                    <Typography variant={`body1_500`} component={"p"}>
                        {t(`common::label::Select all files`, `Select all files`)}
                        &nbsp;
                        <Typography color={theme.palette.primary.main} variant={`body1_500`}>
                            {selectedItems.length}
                        </Typography>
                        /{data.elements?.length}
                    </Typography>
                </Grid>
                {data?.elements?.map((element) => (
                    <Grid
                        item
                        width={72}
                        height={72}
                        sx={{
                            position: `relative`,
                            border: isChecked(element.id) ? `3px solid ${theme.palette.primary.main}` : "none",
                            borderRadius: "4px",
                            cursor: `pointer`,
                        }}
                        key={element.id}
                        onClick={() => onCheckElement(element.id)}
                    >
                        {isChecked(element.id) && (
                            <Grid
                                container
                                justifyContent={"space-between"}
                                sx={{
                                    position: `absolute`,
                                    left: 0,
                                    top: 0,
                                    right: 0,
                                }}
                                p={0.5}
                            >
                                <CircledIcon size={20} color={theme.palette.primary.main}>
                                    <IconCheckLarge size={12} htmlColor={theme.palette.primary.contrastText} />
                                </CircledIcon>
                            </Grid>
                        )}
                        <img
                            src={getElementThumbnailUrl(element)}
                            onError={(e) => {
                                e.target.src = `${PUBLIC_URL}/images/thumbnail_no_image_l.png`;
                            }}
                            alt={""}
                            style={{
                                width: `100%`,
                                height: `100%`,
                                objectFit: `cover`,
                                borderRadius: isChecked(element.id) ? "0px" : `4px`,
                            }}
                        />
                        {data.type === "THUMBNAILS" && (
                            <Box
                                sx={{
                                    position: `absolute`,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: theme.palette.greySecondary.main,
                                    p: "2px 4px",
                                }}
                            >
                                <Typography
                                    variant={"body3_400"}
                                    component={"span"}
                                    color={theme.palette.secondary.contrastText}
                                >
                                    {formatDuration(element.time * 1000)}
                                </Typography>
                            </Box>
                        )}
                    </Grid>
                ))}
            </Grid>
        </Grid>
    );
};

export default SelectMultiImage;
